import {useHttpClient} from '@raison/network-facade';
import {InfoPage, RsnButton, RsnDialog, RsnTextButton} from '@raison/ui-kit';
import {absurd} from 'fp-ts/lib/function';
import {useAtomValue, useSetAtom} from 'jotai';
import React from 'react';
import {useCurrencyFormat} from '~/entities/currencies';
import {Logout$} from '~/entities/user';
import {submitTrtCancellation} from '../api/submitTrtCancellation';
import {CANCELLATION_ENABLED} from '../constants';
import {useSearchModel} from '../lib/useSearchModel';
import {ParamKey, useGoHome} from '../model/SendTxRouting';
import {TrTx} from '../model/TrTx';
import {canceledTrTxIds$} from '../model/TrTxIdsRegistry';
import {TrtDetailsDialog} from './SentTrTxDetailsDialog';
import {AreYouSureCancelDialog} from './views/AreYouSureCancelDialog';

export function TrTxPendingPage({trt}: {trt: TrTx}) {
    const goHome = useGoHome();
    const [params, searchCtl] = useSearchModel({
        [ParamKey.dialog]: {cancel: 'cancel', details: 'details'},
    });

    const {format: formatAsSubject} = useCurrencyFormat(trt.currency);
    const t = {
        Heading: formatAsSubject(trt.amount.negated()),
        Details: 'The transaction will be ready for execution after verifying the specified data.',
        ViewDetails: 'View operation details',
        BackHome: 'Back to home',
        CancelTransaction: 'Cancel the transaction',
    };

    const http = useHttpClient();
    const logout = useAtomValue(Logout$);

    const setCanceledTrt = useSetAtom(canceledTrTxIds$);

    const cancelTrt = async () => {
        if (!CANCELLATION_ENABLED) {
            throw new Error('Cancellation is disabled');
        }
        // optimistically marking transaction as canceled
        const rollback = setCanceledTrt(trt.id);
        const response = await submitTrtCancellation(http, trt.id).catch(error => {
            rollback();
            throw error;
        });
        switch (response.status) {
            case 200:
                return;
            case 401:
                logout();
                break;
            default:
                absurd(response);
        }
    };

    const dialogElement = (() => {
        switch (params.dialog) {
            case 'cancel':
                return <AreYouSureCancelDialog trtId={trt.id} onApprove={cancelTrt} />;
            case 'details':
                return <TrtDetailsDialog trt={trt} amounts={null}  />;
        }
    })();

    return (
        <InfoPage.Layout symbol='time' title={`Send ${trt.currency.code}`}>
            {{
                center: (
                    <InfoPage.Center heading={t.Heading} details={t.Details}>
                        <RsnTextButton.Block decoration='arrow-right' onClick={searchCtl.goTo.dialog.details}>
                            {t.ViewDetails}
                        </RsnTextButton.Block>
                    </InfoPage.Center>
                ),
                footer: (
                    <InfoPage.Footer>
                        <RsnButton.Primary onClick={goHome}>{t.BackHome}</RsnButton.Primary>
                        {CANCELLATION_ENABLED && (
                            <RsnButton.Cautionary onClick={searchCtl.goTo.dialog.cancel}>
                                {t.CancelTransaction}
                            </RsnButton.Cautionary>
                        )}
                        <RsnDialog.Controller onClose={searchCtl.undo.dialog}>{dialogElement}</RsnDialog.Controller>
                    </InfoPage.Footer>
                ),
            }}
        </InfoPage.Layout>
    );
}
